import classNames from 'classnames/bind';
import { motion, useViewportScroll, useTransform } from 'framer-motion';
import { graphql, useStaticQuery } from 'gatsby';
import GatsbyImage from 'gatsby-image';
import debounce from 'lodash/debounce';
import React, { useRef } from 'react';
import { useInView } from 'react-intersection-observer';

import useDeviceType, { DEVICE_TYPE } from 'hooks/use-device-type';
import useSectionOffset from 'hooks/use-section-offset';
import Arrow from 'images/pages/home/misson/arrow.inline.svg';
import Star from 'images/pages/home/misson/star.inline.svg';

import styles from './mission.module.scss';

const cx = classNames.bind(styles);

const MAX_TEXT_SCALE_Y = 18;
const MIN_TEXT_SCALE_Y = 1;

const TICKER_HEIGHT_REM = 4.375;

const variantsIntervals = [0, 1];

const Mission = () => {
  const {
    pair,
    singleExposed,
    single,
    box,
    sticker,
    // eslint-disable-next-line no-use-before-define
  } = useStaticQuery(IMAGE_QUERY);

  const [sectionRef, inView] = useInView({});
  const outerSectionRef = useRef();

  const { scrollY } = useViewportScroll();

  const deviceType = useDeviceType();

  const [sectionStart, sectionEnd, sectionHidden] = useSectionOffset(outerSectionRef);

  const inputRange = variantsIntervals.map(
    (input) => sectionStart + (sectionEnd - sectionStart) * input
  );

  const getTextStyles = () => {
    /* eslint-disable react-hooks/rules-of-hooks */
    const scale = useTransform(scrollY, inputRange, [MAX_TEXT_SCALE_Y, MIN_TEXT_SCALE_Y]);

    if (deviceType === DEVICE_TYPE.mobile.landscape || deviceType === DEVICE_TYPE.mobile.portrait) {
      return { scaleX: 1, scaleY: 1 };
    }
    return { scaleY: scale, scaleX: 1 };
  };

  const getImageStyles = () => {
    /* eslint-disable react-hooks/rules-of-hooks */
    const imagePositionY = useTransform(scrollY, inputRange, ['100vh', '0vh']);
    return { y: imagePositionY, rotate: '-2deg' };
  };

  const getStarStyles = () => {
    /* eslint-disable react-hooks/rules-of-hooks */
    const rotation = useTransform(scrollY, inputRange, [-180, 0]);
    return { rotate: rotation };
  };

  const [isTextCrossed, setIsTextCrossed] = React.useState(false);

  // start crossed text animation when bottom of the section is visible, stop when section is out of view
  React.useEffect(() => {
    const remToPx = (rem) => {
      return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
    };

    const scrollHandler = debounce((evt) => {
      if (
        window.scrollY >= sectionEnd - remToPx(TICKER_HEIGHT_REM) &&
        window.scrollY <= sectionHidden
      ) {
        setIsTextCrossed(true);
      } else {
        setIsTextCrossed(false);
      }
    }, 30);

    if (inView) {
      document.addEventListener('scroll', scrollHandler);
    } else {
      document.removeEventListener('scroll', scrollHandler);
      setIsTextCrossed(false);
    }

    return () => {
      document.removeEventListener('scroll', scrollHandler);
      setIsTextCrossed(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  return (
    <section ref={outerSectionRef} id="about">
      <div className={cx('container', 'row', 'wrapper')} ref={sectionRef}>
        <motion.div className={cx('img-single-exposed')} style={getImageStyles()}>
          <GatsbyImage fluid={singleExposed.childImageSharp.fluid} fadeIn={false} />
        </motion.div>
        <motion.div className={cx('img-pair')}>
          <GatsbyImage fluid={pair.childImageSharp.fluid} fadeIn={false} />
        </motion.div>
        <motion.div className={cx('star')} style={getStarStyles()}>
          <Star />
        </motion.div>
        <div className={cx('arrow')}>
          <Arrow />
        </div>
        <motion.div className={cx('img-single')} style={getImageStyles()}>
          <GatsbyImage fluid={single.childImageSharp.fluid} fadeIn={false} />
        </motion.div>
        <motion.div className={cx('img-sticker')}>
          <GatsbyImage fluid={sticker.childImageSharp.fluid} fadeIn={false} />
        </motion.div>
        <motion.div className={cx('img-box')} style={getImageStyles()}>
          <GatsbyImage fluid={box.childImageSharp.fluid} fadeIn={false} />
        </motion.div>
        <motion.div className={cx('mission-wrapper')} style={getTextStyles()}>
          <p className={cx('mission')}>
            We live in a world where it feels like everything sells out instantly - where it's
            impossible to buy coveted items online. Ghost is here to{' '}
            <span className={cx('mission-crossed', isTextCrossed && 'mission-crossed-animated')}>
              remove this stress
            </span>{' '}
            and to bring back the fun in buying limited items online by providing innovative tools
            and resources tailored to your exact needs, whether it's a one-time necessity or a
            full&#8209;time&nbsp;passion.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Mission;

const IMAGE_QUERY = graphql`
  query {
    pair: file(relativePath: { eq: "pages/home/misson/pair.png" }) {
      id
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
    singleExposed: file(relativePath: { eq: "pages/home/misson/single-exposed.png" }) {
      id
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
    box: file(relativePath: { eq: "pages/home/misson/box.png" }) {
      id
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
    single: file(relativePath: { eq: "pages/home/misson/single.png" }) {
      id
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
    sticker: file(relativePath: { eq: "pages/home/misson/sticker.png" }) {
      id
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
  }
`;
